import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, Instagram, Loader2, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import {
  beginInstagramConnect,
  publishInstagramConnectionCompletion,
  rememberInstagramConnectionConfirmation,
} from '../../utils/instagramConnect';
import { getDefaultRouteForUser } from '../../utils/onboardingStatus';
import './InstagramConnectionReturn.scss';

const InstagramConnectionReturn = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, refreshUser } = useAuth();
  const [checking, setChecking] = useState(true);
  const [profile, setProfile] = useState(null);
  const [resolvedUser, setResolvedUser] = useState(currentUser);
  const [launching, setLaunching] = useState(false);
  const currentUserRef = useRef(currentUser);
  const refreshUserRef = useRef(refreshUser);

  const storedReturnTo = sessionStorage.getItem('foth_instagram_return_to');
  const invitedOfferId = currentUser?.creator_profile?.invited_offer_ids?.[0]
    || currentUser?.creator_profile?.invited_collab;
  const profileReturnTo = invitedOfferId
    ? `/creator/offers/${invitedOfferId}`
    : '';
  const explicitNextPath = searchParams.get('next') || storedReturnTo || profileReturnTo || '';
  const error = searchParams.get('error');
  const oauthConnected = searchParams.get('instagram_connected') === 'true';
  const isPopupReturn = searchParams.get('popup') === '1';
  const attemptId = searchParams.get('attempt_id') || '';

  useEffect(() => {
    currentUserRef.current = currentUser;
    refreshUserRef.current = refreshUser;
  }, [currentUser, refreshUser]);

  useEffect(() => {
    let mounted = true;
    const checkConnection = async () => {
      setChecking(true);
      const refreshed = await refreshUserRef.current?.();
      if (!mounted) return;
      const user = refreshed || currentUserRef.current;
      setResolvedUser(user || null);
      const nextProfile = user?.user_type === 'business' ? user?.business_profile : user?.creator_profile;
      setProfile(nextProfile || null);
      setChecking(false);
    };

    checkConnection();
    return () => { mounted = false; };
  }, []);

  const connected = useMemo(() => (
    Boolean(profile?.instagram_account_connected) || oauthConnected
  ), [profile, oauthConnected]);

  const nextPath = useMemo(() => {
    const defaultPath = getDefaultRouteForUser(resolvedUser || currentUser);
    if (['/complete-profile', '/select-role'].includes(defaultPath)) {
      return defaultPath;
    }
    return explicitNextPath || defaultPath;
  }, [currentUser, explicitNextPath, resolvedUser]);

  useEffect(() => {
    if (!checking && connected && !error) {
      const timer = setTimeout(() => {
        rememberInstagramConnectionConfirmation({
          returnTo: nextPath,
          userType: currentUserRef.current?.user_type,
          username: profile?.ig_account || profile?.instagram_account || profile?.ig_name || '',
        });
        sessionStorage.removeItem('foth_instagram_return_to');
        sessionStorage.removeItem('foth_instagram_callback_target');
        if (isPopupReturn) {
          publishInstagramConnectionCompletion({ returnTo: nextPath, attemptId });
          window.close();
          return;
        }
        navigate(nextPath, { replace: true });
      }, 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [attemptId, checking, connected, error, isPopupReturn, navigate, nextPath, profile]);

  const retry = () => {
    if (launching) return;
    setLaunching(true);
    beginInstagramConnect(nextPath, { attemptId });
    window.setTimeout(() => setLaunching(false), 3000);
  };

  return (
    <div className="instagram-return-page">
      <motion.div
        className="instagram-return-card"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className={`instagram-return-icon ${connected && !error ? 'success' : 'warning'}`}>
          {checking ? <Loader2 className="spin" size={28} /> : connected && !error ? <CheckCircle2 size={30} /> : <AlertTriangle size={30} />}
        </div>

        <div className="instagram-return-eyebrow">
          <Instagram size={14} />
          FOTH · Instagram
        </div>

        <h1>
          {checking
            ? t('instagram_connection.return.checking_title')
            : connected && !error
              ? t('instagram_connection.return.success_title')
              : t('instagram_connection.return.error_title')}
        </h1>

        <p>
          {checking
            ? t('instagram_connection.return.checking_body')
            : connected && !error
              ? t('instagram_connection.return.success_body')
              : t('instagram_connection.return.error_body')}
        </p>

        {!checking && connected && !error && (
          <div className="instagram-return-success-note">
            <CheckCircle2 size={16} />
            <span>{t('instagram_connection.return.sync_note')}</span>
          </div>
        )}

        {!checking && (!connected || error) && (
          <div className="instagram-return-actions">
            <button className="primary" onClick={retry} disabled={launching}>
              <RefreshCw size={16} />
              {launching ? t('instagram_connection.return.opening') : t('instagram_connection.return.retry')}
            </button>
            <button className="secondary" onClick={() => navigate(nextPath, { replace: true })}>
              {t('instagram_connection.return.back')}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default InstagramConnectionReturn;
